/*
  A picture for a figure, where there is one worth drawing.

  The unit is the wrong thing to choose on. Two revenue figures in dollars, one with three
  sources that disagree and one with a single source, are the same metric and the same unit
  and want completely different pictures. What decides the chart is what the box has to
  compare itself against, so the choice is made in that order and the first match wins:

    two or more sources        the spread between them
    the same metric over time  a sparkline
    a year-to-date figure      how much of the year produced it
    a lone percentage          the proportion it is

  A dollar or count figure with one source and one period gets nothing, deliberately. There
  is no comparison to draw, and a full-width bar behind it would encode no information while
  looking exactly like the boxes that do carry one. Empty is the honest answer and it also
  makes the boxes that have something to say visible from across the page.
*/

export type Unit = 'usd' | 'count' | 'percent'

export interface ChartSource {
  value: number
  kind: string
  kindLabel: string
  dismissed: boolean
  superseded: boolean
}

export interface ChartPoint {
  periodEnd: string | null
  periodLabel: string
  value: number
}

/**
 * Whether a move is an improvement, in the three states this can honestly be in.
 *
 * Shared with the delta chip on the card face so the arrow and the sparkline can never
 * disagree with each other about the same pair of numbers.
 *
 * 'flat' is the default and covers two different situations on purpose: the figure did not
 * move, and nobody has said which way is good. Painting an unknown direction green is worse
 * than leaving it grey, because afterwards the owner cannot tell the two apart.
 */
export type Tone = 'flat' | 'good' | 'bad'

export function toneOf(from: number, to: number, higherIsBetter: boolean | null): Tone {
  if (higherIsBetter === null || to === from) return 'flat'
  return (to > from) === higherIsBetter ? 'good' : 'bad'
}

/**
 * The change since the previous period, formatted the way the unit actually moves.
 *
 * Percentage points, not percent. Gross margin going from 34% to 41% moved seven points,
 * and calling that "+20%" is a different and wrong claim about the same two numbers.
 */
export function deltaLabel(from: number, to: number, unit: Unit): string {
  const d = to - from
  const sign = d > 0 ? '+' : '-'
  const size = Math.abs(d)

  if (unit === 'usd') {
    const rounded = size >= 1000 ? Math.round(size / 100) / 10 + 'k' : Math.round(size).toString()
    return `${sign}$${rounded}`
  }

  if (unit === 'percent') {
    const pts = Math.round(size * 10) / 10
    return `${sign}${pts} ${pts === 1 ? 'pt' : 'pts'}`
  }

  return `${sign}${Math.round(size)}`
}

export function FigureChart({
  unit, value, low, high, sources, history, elapsedPct, partial, higherIsBetter, conflicted,
}: {
  unit: Unit
  value: number
  low: number
  high: number
  sources: ChartSource[]
  /** The same metric in other periods, oldest first. Empty when there is only this one. */
  history: ChartPoint[]
  /** How far through the fiscal year today is, for a year-to-date figure. */
  elapsedPct: number
  partial: boolean
  /** Null where nobody has said which way is good, which is most of them. */
  higherIsBetter: boolean | null
  conflicted: boolean
}) {
  const live = sources.filter(s => !s.dismissed && !s.superseded)

  if (live.length > 1) {
    return <Spread value={value} low={low} high={high} sources={live} conflicted={conflicted} />
  }

  if (history.length > 1) {
    return <Trend points={history} higherIsBetter={higherIsBetter} />
  }

  if (partial) return <Elapsed elapsedPct={elapsedPct} />

  if (unit === 'percent') return <Proportion value={value} />

  return null
}

/*
  Where the sources sit.

  The screen's whole reason for existing is that two sources disagreed and it refuses to
  pick, so the disagreement should be the picture rather than a list of rows underneath. The
  line spans low to high, each source is a dot on it, and the agreed value is the mark.
*/
function Spread({
  value, low, high, sources, conflicted,
}: {
  value: number; low: number; high: number; sources: ChartSource[]; conflicted: boolean
}) {
  // A span of zero means they agreed exactly, and dividing by it puts every dot at NaN%.
  const span = high - low
  const at = (v: number) => (span === 0 ? 50 : ((v - low) / span) * 100)

  return (
    <div className={`fig fig--spread${conflicted ? ' fig--conflict' : ''}`}>
      <span className="fig__rail" />
      {sources.map((s, i) => (
        <span
          key={i}
          className={`fig__dot fig__dot--${s.kind}`}
          style={{ left: `${at(s.value)}%` }}
          title={`${s.kindLabel}: ${s.value}`}
        />
      ))}
      <span className="fig__mark" style={{ left: `${at(value)}%` }} />
    </div>
  )
}

/*
  The same metric, over the periods there are.

  Coloured only where the product knows which way is good. Rising is not improving: a
  callback rate climbing is bad news and a close rate climbing is good, and a chart that
  paints every rise green teaches an owner to stop reading the colour.
*/
function Trend({
  points, higherIsBetter,
}: {
  points: ChartPoint[]; higherIsBetter: boolean | null
}) {
  const values = points.map(p => p.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1

  const W = 100
  const H = 28
  const x = (i: number) => (points.length === 1 ? W / 2 : (i / (points.length - 1)) * W)
  const y = (v: number) => H - ((v - min) / span) * (H - 6) - 3

  const line = points.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ')

  // The same line closed along the bottom. A stroke alone is nearly invisible once the
  // viewBox is stretched to the width of a card, because the horizontal stretch flattens
  // every slope; the area under it carries the shape that the line on its own loses.
  const area = `${line} ${x(points.length - 1).toFixed(1)},${H} 0,${H}`

  const first = values[0]!
  const last = values[values.length - 1]!
  const tone = toneOf(first, last, higherIsBetter)

  return (
    <div className="fig fig--trend">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden focusable="false">
        <polygon className={`fig__area fig__area--${tone}`} points={area} />
        <polyline className={`fig__line fig__line--${tone}`} points={line} />
      </svg>

      {/*
        The last reading, as a positioned element rather than an SVG circle. The chart is
        stretched to the width of its card with preserveAspectRatio="none", which turns any
        circle inside it into an ellipse; vector-effect saves the stroke width and nothing
        saves the geometry.
      */}
      <span
        className={`fig__end fig__end--${tone}`}
        style={{ left: '100%', top: `${(y(last) / H) * 100}%` }}
      />

      <span className="fig__span">
        {points[0]!.periodLabel} to {points[points.length - 1]!.periodLabel}
      </span>
    </div>
  )
}

/*
  How much of the year is behind this number.

  The one chart here that no other product would have, because it comes from a rule this one
  had to learn: eight months of a year is not a year. A year-to-date figure sitting next to
  the fraction of the year that produced it is that rule drawn rather than explained.
*/
function Elapsed({ elapsedPct }: { elapsedPct: number }) {
  const pct = Math.max(0, Math.min(100, elapsedPct))

  return (
    <div className="fig fig--elapsed">
      <span className="fig__bar">
        <span className="fig__barfill" style={{ width: `${pct}%` }} />
      </span>
      <span className="fig__span">{pct}% of the year so far</span>
    </div>
  )
}

/** A percentage is already a proportion of a hundred, so it is drawn as one. */
function Proportion({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, value))

  return (
    <div className="fig fig--proportion">
      <span className="fig__bar">
        <span className="fig__barfill" style={{ width: `${pct}%` }} />
      </span>
    </div>
  )
}
